import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ArrowRight, Clock } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog-posts";

export default function BlogPage() {
  return (
    <>
      <Helmet>
        <title>Blog — ScribPost AI Writing Tips & LinkedIn Strategies</title>
        <meta name="description" content="Practical tips on writing LinkedIn posts, building your personal brand with AI, and creating content that sounds authentically you." />
        <link rel="canonical" href="https://scribpost.com/blog" />
      </Helmet>

      <div className="min-h-screen bg-[#0D1A2A] text-white">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D1A2A]/90 backdrop-blur-md border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2 font-bold text-xl">
              <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
              <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link href="/blog" className="text-white font-medium">Blog</Link>
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/login" className="text-sm text-slate-300 hover:text-white transition-colors hidden sm:block">
                Log in
              </Link>
              <Link
                href="/start"
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white"
              >
                Start Free
              </Link>
            </div>
          </div>
        </nav>

        <div className="pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">The ScribPost Blog</h1>
              <p className="text-slate-400 text-lg">
                Practical tips on LinkedIn growth, AI writing, and building a personal brand that sounds like you.
              </p>
            </div>

            <div className="space-y-6">
              {BLOG_POSTS.map((post) => (
                <Link
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                  className="block p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-cyan-500/20 transition-all group"
                >
                  <div className="flex items-center gap-3 mb-3 text-xs text-slate-500">
                    <span>{post.date}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                    <span>·</span>
                    <span>{post.author}</span>
                  </div>
                  <h2 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors leading-snug">
                    {post.title}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-1 text-cyan-400 text-sm font-medium">
                    Read article <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-white/10 py-10 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Link href="/" className="flex items-center gap-2 font-bold text-lg">
                <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
              </Link>
              <nav className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <Link href="/about" className="hover:text-white transition-colors">About</Link>
                <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              </nav>
              <p className="text-xs text-slate-600">© 2026 ScribPost. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
